import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Box } from '@mui/system';
import React from 'react';
import { Link, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import {
	AuthContext,
	AuthData,
	CORE_SEARCH_ENABLED,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from '../context/auth';
import { ConfigContext, ConfigData } from '../context/config';
import { ADVANCED_SEARCH_LABEL } from '../data/labelsAndMessages';
import i18n from '../i18n';
import { TenantAdminApp } from './admin/tenant';
import AuxDataView from './auxData/AuxDataView';
import { CatchUpInvoicePage } from './catchUps';
import CatchUpsPage from './catchUps/pages/CatchUpsPage';
import ClaimRoutes from './claims/ClaimRoutes';
import CoreAdvancedSearchView from './coreSearch/CoreAdvancedSearchView';
import Dashboard from './dashboard/Dashboard';
import {
	DisbursementPage,
	DisbursementsPage,
	EditDisbursementPage,
} from './disbursements';
import HealthCheck from './HealthCheck';
import LargeCenterSpinner from './LargeCenterSpinner';
import CoreUILayout from './layouts/CoreUILayout';
import FullPageLayout from './layouts/FullPageLayout';
import PolicyholderLayout from './layouts/PolicyholderLayout';
import Login from './Login';
import Logout from './Logout';
import {
	CreatePaymentPage,
	EditPaymentPage,
	PaymentPage,
	PaymentsPage,
} from './payments';
import PaymentsLayout from './payments/components/PaymentsLayout';
import NewApplicationView from './policy/NewApplicationView';
import PolicyQuoteRoutes from './policy/PolicyQuoteRoutes';
import CancellationRoutes from './policy/Routes/CancellationRoutes';
import EndorsementRoutes from './policy/Routes/EndorsementRoutes';
import ReinstatementRoutes from './policy/Routes/ReinstatementRoutes';
import RenewalRoutes from './policy/Routes/RenewalRoutes';
import CreatePolicyholder from './policyholder/CreatePolicyholder';
import InvoicesView from './policyholder/InvoicesView';
import InvoiceView from './policyholder/InvoiceView';
import PoliciesView from './policyholder/PoliciesView';
import PolicyholderOverview from './policyholder/PolicyholderOverview';
import PremiumReportingRoutes from './premiumReports/PremiumReportingRoutes';
import ProtectedRoute from './ProtectedRoute';
import ReportsView from './reports/ReportsView';
import { SEARCH_DOCS_URL } from './search/constants';
import UnifiedLoginPage from './UnifiedLoginPage';
import VersionCheck from './Version';

interface Props {
	authData: AuthData;
	configData: ConfigData;
}

class ApplicationRoutes extends React.Component<Props, {}> {
	public state = {
		error: false,
	};

	async componentDidMount() {
		if (!this.props.authData.auth) {
			this.props.authData.getLoggedInAccount();
		}
		if (!this.props.configData.productConfigs) {
			this.props.configData.loadConfigFromUserData();
		}
	}

	public render() {
		const { authData, configData } = this.props;
		const cashManagementEnabled =
			authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
		if (
			!authData.auth &&
			!authData.adminAuthData &&
			!this.props.authData.loading
		) {
			return (
				<Routes>
					<Route path='/'>
						<Route index element={<Login />} />
						<Route path='health' element={<HealthCheck />} />
						<Route path='version' element={<VersionCheck />} />
						<Route path='logout' element={<Logout authData={authData} />} />
						<Route path='unified-login' element={<UnifiedLoginPage />} />
						<Route path='*' element={<Navigate to='/' />} />
					</Route>
					{/* /heath and /version are used for kubernetes health checks */}
				</Routes>
			);
		} else if (authData.auth) {
			if (
				authData.auth.configProperties[
					'property.ui.life.insurance.localization.enabled'
				] === true
			) {
				if (i18n.language !== 'life') {
					// change language to life
					i18n.changeLanguage('life');
				}
			} else {
				if (i18n.language !== 'default') {
					// language should be default
					i18n.changeLanguage('default');
				}
			}

			if (
				!configData.productConfigs ||
				!configData.paymentConfiguration ||
				!configData.tenantConfiguration ||
				!configData?.policyholderCardConfig?.workflows
			) {
				return <LargeCenterSpinner />;
			}

			return (
				<Routes>
					<Route element={<CoreUILayout />}>
						<Route
							path='policy/:policyId/premiumReports/:premiumReportId/*'
							element={
								<ProtectedRoute permission='premiumReport.fetch'>
									<PremiumReportingRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							path='policy/:policyId/cancellations/:cancellationId/reinstatements/:reinstatementId/*'
							element={
								<ProtectedRoute
									permission={['cancellation.fetch', 'reinstatement.fetch']}
								>
									<ReinstatementRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							path='policy/:policyId/cancellations/:cancellationId/*'
							element={
								<ProtectedRoute permission='cancellation.fetch'>
									<CancellationRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							path='policy/:policyId/endorsements/:endorsementId/*'
							element={
								<ProtectedRoute permission='endorsement.fetch'>
									<EndorsementRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							path='policy/:policyId/renewals/:renewalId/*'
							element={
								<ProtectedRoute permission='renewal.fetch'>
									<RenewalRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							path='policy/:policyId/claims/:claimId/*'
							element={
								<ProtectedRoute permission='claim.fetch'>
									<ClaimRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							path='policy/:policyId/*'
							element={
								<ProtectedRoute permission='policy.fetch'>
									<PolicyQuoteRoutes />
								</ProtectedRoute>
							}
						/>
						<Route
							element={<FullPageLayout title={<>Start New Application</>} />}
							path='startNewApplication'
						>
							<Route
								index
								element={
									<ProtectedRoute
										permission={[
											'policyholder.fetch',
											'policy.create',
											'quote.fetch',
										]}
									>
										<NewApplicationView />
									</ProtectedRoute>
								}
							/>
						</Route>
						<Route path='logout' element={<Logout authData={authData} />} />
						{cashManagementEnabled && (
							<Route
								path='payments'
								element={
									<ProtectedRoute permission={'payment.fetch'}>
										<PaymentsLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<PaymentsPage />} />
								<Route path='new' element={<CreatePaymentPage />} />
								<Route path=':paymentLocator'>
									<Route index element={<PaymentPage />} />
									<Route path='edit' element={<EditPaymentPage />} />
								</Route>
							</Route>
						)}
						<Route path='policyholder'>
							<Route
								path='new'
								element={<FullPageLayout title={<>Create Policyholder</>} />}
							>
								<Route
									index
									element={
										<ProtectedRoute
											permission={[
												'policyholder.create',
												'policyholder.update',
											]}
										>
											<CreatePolicyholder />
										</ProtectedRoute>
									}
								/>
							</Route>
							<Route
								path=':policyholderId'
								element={
									<ProtectedRoute permission={['policyholder.fetch']}>
										<PolicyholderLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<Navigate to='overview' replace />} />
								<Route path='overview' element={<PolicyholderOverview />} />
								<Route
									path='policies'
									element={
										<ProtectedRoute permission={'policy.fetch'}>
											<PoliciesView />
										</ProtectedRoute>
									}
								/>
								<Route
									path='auxData'
									element={
										<ProtectedRoute permission={'auxData.fetch'}>
											<AuxDataView
												entityPath='policyholderId'
												entityType='policyholder'
												backUrl='../'
											/>
										</ProtectedRoute>
									}
								/>
								<Route path='invoices'>
									<Route
										index
										element={
											<ProtectedRoute permission={'invoice.fetch'}>
												<InvoicesView />
											</ProtectedRoute>
										}
									/>
									<Route path=':invoiceId'>
										<Route
											index
											element={
												<ProtectedRoute permission={'invoice.fetch'}>
													<InvoiceView />
												</ProtectedRoute>
											}
										/>
									</Route>
								</Route>
								{cashManagementEnabled && (
									<Route
										path='payments'
										element={
											<ProtectedRoute permission={'payment.fetch'}>
												<Outlet />
											</ProtectedRoute>
										}
									>
										<Route index element={<PaymentsPage />} />
										<Route path='new' element={<CreatePaymentPage />} />
										<Route path=':paymentLocator'>
											<Route index element={<PaymentPage />} />
											<Route path='edit' element={<EditPaymentPage />} />
										</Route>
									</Route>
								)}
								{cashManagementEnabled && (
									<>
										<Route
											path='disbursements'
											element={
												<ProtectedRoute permission={'disbursement.fetch'}>
													<Outlet />
												</ProtectedRoute>
											}
										>
											<Route index element={<DisbursementsPage />} />
											<Route path=':disbursementLocator'>
												<Route index element={<DisbursementPage />} />
											</Route>
											<Route path=':disbursementLocator/edit'>
												<Route index element={<EditDisbursementPage />} />
											</Route>
										</Route>
										<Route
											path='catchUps'
											element={
												<ProtectedRoute permission={'catchUpInvoice.fetch'}>
													<Outlet />
												</ProtectedRoute>
											}
										>
											<Route index element={<CatchUpsPage />} />
											<Route path=':catchUpLocator'>
												<Route index element={<CatchUpInvoicePage />} />
											</Route>
										</Route>
									</>
								)}
							</Route>
						</Route>
						<Route
							path='reports'
							element={<FullPageLayout title={<>Reports</>} />}
						>
							<Route
								index
								element={
									<ProtectedRoute permission={'report.fetch'}>
										<ReportsView />
									</ProtectedRoute>
								}
							/>
						</Route>
						{authData?.auth?.configProperties?.[CORE_SEARCH_ENABLED] && (
							<Route
								path='search'
								element={
									<FullPageLayout
										title={
											<Box display='flex'>
												{ADVANCED_SEARCH_LABEL()}
												<Link
													to={SEARCH_DOCS_URL}
													target='_blank'
													rel='noopener noreferrer'
												>
													<DescriptionOutlinedIcon
														sx={{ ml: 1, color: 'black.main' }}
													/>
												</Link>
											</Box>
										}
									/>
								}
							>
								<Route
									index
									element={
										<ProtectedRoute permission={'search.fetch'}>
											<CoreAdvancedSearchView />
										</ProtectedRoute>
									}
								/>
							</Route>
						)}
						<Route path='/' element={<FullPageLayout title={<>Overview</>} />}>
							<Route index element={<Dashboard />} />
						</Route>
						<Route path='*' element={<Navigate to='/' />} />
					</Route>
				</Routes>
			);
		} else {
			return <TenantAdminApp />;
		}
	}
}

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData'>) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(ApplicationRoutes);
